import * as React from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Image from 'mui-image';
import ProfileButton from './accountButton';
import { Container, Button, useMediaQuery, Breadcrumbs, Link } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const pages = [
  {title: 'About Us', link: '/about_us'}, 
  {title: 'Blog', link: 'https://blog.capsized-fantasy.com'}
];

function ResponsiveAppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme<Theme>();
  const { leagueId } = useParams<{ leagueId: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const user = useAppSelector((state) => state.user);
  const isRoot = location.pathname === '/';
  const isInLeague = Boolean(leagueId);
  
  // For now, just show basic league context without full data loading
  // This avoids API calls on non-league pages while still providing useful context
  
  // Generate breadcrumbs based on current path
  const breadcrumbs = React.useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const crumbs = [{ label: 'Home', path: '/' }];
    
    if (isInLeague) {
      crumbs.push({ label: 'League', path: `/league/${leagueId}` });
      
      // Add specific page breadcrumbs
      if (pathSegments.length > 2) {
        const pageName = pathSegments[2];
        const pageLabels: { [key: string]: string } = {
          'powerRankings': 'Power Rankings',
          'matchup': 'Current Matchup',
          'players': 'Players & Waivers',
          'trade': 'Trade Builder',
          'team': 'Team',
          'standings': 'Standings',
          'schedule': 'Schedule',
          'transactions': 'Transactions',
          'picks': 'Draft Picks',
          'contracts': 'Contracts',
          'settings': 'Settings'
        };
        
        if (pageLabels[pageName]) {
          crumbs.push({ 
            label: pageLabels[pageName], 
            path: location.pathname 
          });
        }
      }
    }
    
    return crumbs;
  }, [location.pathname, isInLeague, leagueId]);

  const handleNavMenuSelection = (page: string) => {
    if (page.startsWith('/')) {
      navigate(page);
    } else {
      window.open(page, '_blank');
    }
  };

  return (
    <AppBar 
      position="fixed"
      elevation={0} 
      sx={{
        paddingTop: { xs: 'calc(env(safe-area-inset-top, 0px) + 14px)', sm: '14px' },
        paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', sm: 0 },
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'var(--primary-color)',
        boxShadow: 'none',
        transition: 'background-color 0.3s ease-in-out',
        '&.MuiAppBar-root': {
          backgroundColor: 'var(--primary-color)'
        }
      }}
    >
      <Container maxWidth="xl" sx={{ height: '100%' }}>
        {/* Main Toolbar */}
        <Toolbar disableGutters sx={{ 
          minHeight: { xs: '56px !important' }, 
          height: '100%'
        }}>
          {/* Logo and Title - Left Aligned */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            flexGrow: 1,
            minWidth: 0 // Allows text truncation
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flexShrink: 0,
              maxWidth: { xs: 'calc(100% - 120px)', sm: 'none' } // More space for text on mobile
            }}>
              <Image 
                style={{
                  width: '40px',
                  flexShrink: 0,
                  [theme.breakpoints.up('md')]: {
                    width: '60px'
                  }
                }}
                src="/assets/image/Capsized_Logo.png" 
                alt="Capsized Fantasy Logo"
              />
              <Typography
                variant="h6"
                noWrap
                component={isRoot ? 'p' : 'a'}
                onClick={isRoot ? undefined : (() => handleNavMenuSelection('/'))}
                sx={{
                  ml: 1,
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  letterSpacing: '.3rem',
                  color: 'inherit',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  flexShrink: 1,
                  minWidth: 0, // Allows text truncation to work properly
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '100%',
                  padding: '0 8px',
                  margin: '0 -8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease-in-out',
                  ...(isRoot ? {} : {
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  })
                }}
              >
                CAPSized
              </Typography>
            </Box>
            
          </Box>

          {/* Desktop Navigation - Hidden on mobile */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mr: 2
          }}>
          {user && user.roles && user.roles.indexOf('admin') !== -1 && 
            <Button
              onClick={() => handleNavMenuSelection('/admin')}
              sx={{ 
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                px: 2,
                py: 1,
                borderRadius: 1
              }}
            >
              Admin
            </Button>
          }
            {pages.map((page) => (
              <Button
                key={page.link}
                onClick={() => handleNavMenuSelection(page.link)}
                sx={{ 
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  px: 2,
                  py: 1,
                  borderRadius: 1
                }}
              >
                {page.title}
              </Button>
            ))}
          </Box>

          {/* Profile Section - Always visible */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            ml: 1
          }}>
            <ProfileButton />
          </Box>
        </Toolbar>
        
        {/* Breadcrumbs - Below main toolbar */}
        {breadcrumbs.length > 1 && !isRoot && (
          <Box sx={{ 
            pb: 1, 
            px: { xs: 1, sm: 0 },
            display: { xs: 'none', sm: 'block' } // Hide on mobile to save space
          }}>
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />}
              sx={{
                '& .MuiBreadcrumbs-ol': {
                  flexWrap: 'nowrap',
                },
                '& .MuiBreadcrumbs-li': {
                  minWidth: 0,
                }
              }}
            >
              {breadcrumbs.map((crumb, index) => (
                <Link
                  key={crumb.path}
                  component="button"
                  variant="body2"
                  onClick={() => navigate(crumb.path)}
                  sx={{
                    color: index === breadcrumbs.length - 1 ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'none',
                    fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                    fontSize: '0.875rem',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {crumb.label}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
        )}
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;